import React, { useState, useEffect } from 'react';
import Icon from '../../shared/Icon';
import './SharePointConnectionForm.css';

/**
 * SharePoint connection form (PRD-806B).
 *
 * Presentational only — owns its local field state, hands the serialized payload
 * to the parent via `onChange` whenever any field changes. Parent (AddConnectionModal)
 * decides when to POST.
 *
 * Required Azure AD app permissions on the customer's tenant: Sites.Read.All +
 * Files.Read.All with admin consent. Customer-side setup is documented in the
 * onboarding guide.
 *
 * `initialValues` (edit mode) seeds every field except `client_secret`,
 * which never leaves Key Vault. `lockSecret` is forwarded for the secret
 * input — empty in edit mode means "keep existing vault value."
 */
const SharePointConnectionForm = ({ onChange, initialValues, lockSecret }) => {
  const initial = initialValues || {};
  const [tenantId, setTenantId] = useState(initial.tenant_id || '');
  const [clientId, setClientId] = useState(initial.client_id || '');
  const [clientSecret, setClientSecret] = useState('');
  const [siteUrl, setSiteUrl] = useState(initial.site_url || '');
  const [folderPath, setFolderPath] = useState(initial.folder_path || '/');
  const [includeSubfolders, setIncludeSubfolders] = useState(
    typeof initial.include_subfolders === 'boolean' ? initial.include_subfolders : true
  );
  const [fileExtensions, setFileExtensions] = useState(
    Array.isArray(initial.file_extensions) ? initial.file_extensions.join(', ') : (initial.file_extensions || '')
  );
  // Tenant/Client IDs aren't secrets but are sensitive enough to mask by
  // default — matches the pattern Azure/AWS consoles use.
  const [tenantIdVisible, setTenantIdVisible] = useState(false);
  const [clientIdVisible, setClientIdVisible] = useState(false);

  // Edit mode: emit the seeded payload up so the parent validates correctly
  // even if the user submits without touching anything.
  useEffect(() => {
    if (!initialValues) return;
    onChange({
      tenant_id: initial.tenant_id || '',
      client_id: initial.client_id || '',
      client_secret: '',
      site_url: initial.site_url || '',
      folder_path: initial.folder_path || '/',
      include_subfolders: typeof initial.include_subfolders === 'boolean' ? initial.include_subfolders : true,
      file_extensions: Array.isArray(initial.file_extensions) ? initial.file_extensions : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent whenever any field changes — parent reads the latest payload
  // when the user submits. Stored as a ref-like inline call so we don't need
  // useEffect (which would lag by one render).
  const emit = (overrides = {}) => {
    const next = {
      tenant_id: overrides.tenantId ?? tenantId,
      client_id: overrides.clientId ?? clientId,
      client_secret: overrides.clientSecret ?? clientSecret,
      site_url: overrides.siteUrl ?? siteUrl,
      folder_path: (overrides.folderPath ?? folderPath) || '/',
      include_subfolders: overrides.includeSubfolders ?? includeSubfolders,
      file_extensions: parseExtensions(overrides.fileExtensions ?? fileExtensions),
    };
    onChange(next);
  };

  return (
    <div className="sharepoint-form">
      <div className="connection-modal-field">
        <label htmlFor="sp-tenant-id">Tenant ID</label>
        <div style={{ position: 'relative', width: '100%' }}>
          <input className="resui-input"
            id="sp-tenant-id"
            type={tenantIdVisible ? 'text' : 'password'}
            value={tenantId}
            onChange={(e) => {
              setTenantId(e.target.value);
              emit({ tenantId: e.target.value });
            }}
            placeholder="00000000-0000-0000-0000-000000000000"
            required
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setTenantIdVisible((v) => !v)}
            aria-label={tenantIdVisible ? 'Hide Tenant ID' : 'Show Tenant ID'}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
              color: 'var(--color-icon-grey)', display: 'flex', alignItems: 'center',
            }}
          >
            <Icon name={tenantIdVisible ? 'visibility_off' : 'visibility'} style={{ fontSize: 18 }} />
          </button>
        </div>
        <p className="connection-modal-helper-text">Your Microsoft Entra (Azure AD) tenant ID.</p>
      </div>

      <div className="connection-modal-field">
        <label htmlFor="sp-client-id">Client ID</label>
        <div style={{ position: 'relative', width: '100%' }}>
          <input className="resui-input"
            id="sp-client-id"
            type={clientIdVisible ? 'text' : 'password'}
            value={clientId}
            onChange={(e) => {
              setClientId(e.target.value);
              emit({ clientId: e.target.value });
            }}
            placeholder="Azure AD app (client) ID"
            required
            style={{ paddingRight: 40 }}
          />
          <button
            type="button"
            onClick={() => setClientIdVisible((v) => !v)}
            aria-label={clientIdVisible ? 'Hide Client ID' : 'Show Client ID'}
            style={{
              position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
              background: 'transparent', border: 'none', cursor: 'pointer', padding: 2,
              color: 'var(--color-icon-grey)', display: 'flex', alignItems: 'center',
            }}
          >
            <Icon name={clientIdVisible ? 'visibility_off' : 'visibility'} style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>

      <div className="connection-modal-field">
        <label htmlFor="sp-client-secret">Client Secret</label>
        <input className="resui-input"
          id="sp-client-secret"
          type="password"
          value={clientSecret}
          onChange={(e) => {
            setClientSecret(e.target.value);
            emit({ clientSecret: e.target.value });
          }}
          placeholder={lockSecret ? '' : 'App client secret'}
          required={!lockSecret}
          autoComplete="new-password"
        />
        <p className="connection-modal-helper-text">
          {lockSecret
            ? 'Leave blank to keep the existing secret in Azure Key Vault. Type a new value to rotate.'
            : 'Stored in Azure Key Vault. Never persisted to the database in plaintext.'}
        </p>
      </div>

      <div className="connection-modal-field">
        <label htmlFor="sp-site-url">Site URL</label>
        <input className="resui-input"
          id="sp-site-url"
          type="url"
          value={siteUrl}
          onChange={(e) => {
            setSiteUrl(e.target.value);
            emit({ siteUrl: e.target.value });
          }}
          placeholder="https://contoso.sharepoint.com/sites/Engineering"
          required
        />
        <p className="connection-modal-helper-text">
          Full URL to your SharePoint site.
        </p>
      </div>

      <div className="connection-modal-field">
        <label htmlFor="sp-folder-path">Folder Path (Optional)</label>
        <input className="resui-input"
          id="sp-folder-path"
          type="text"
          value={folderPath}
          onChange={(e) => {
            setFolderPath(e.target.value);
            emit({ folderPath: e.target.value });
          }}
          placeholder="/Inspections"
        />
        <p className="connection-modal-helper-text">
          Scope ingestion to a folder within the site's default document library. Leave as '/' for the whole library.
        </p>
      </div>

      <div className="connection-modal-field sharepoint-form-checkbox">
        <label className="resui-checkbox">
          <input
            type="checkbox"
            className="resui-checkbox-input"
            checked={includeSubfolders}
            onChange={(e) => {
              setIncludeSubfolders(e.target.checked);
              emit({ includeSubfolders: e.target.checked });
            }}
          />
          <span className="resui-checkbox-mark" />
          <span className="resui-checkbox-label">Include subfolders</span>
        </label>
      </div>

      <div className="connection-modal-field">
        <label htmlFor="sp-file-extensions">File Extensions (Optional)</label>
        <input className="resui-input"
          id="sp-file-extensions"
          type="text"
          value={fileExtensions}
          onChange={(e) => {
            setFileExtensions(e.target.value);
            emit({ fileExtensions: e.target.value });
          }}
          placeholder="pdf, csv, json"
        />
        <p className="connection-modal-helper-text">
          Comma-separated whitelist. Leave blank to accept every file. Case-insensitive; leading dot optional.
        </p>
      </div>
    </div>
  );
};

/**
 * Parse a comma-separated extension list into the backend's expected
 * array form. Empty / whitespace-only input returns null so the backend
 * treats it as "accept any extension".
 */
function parseExtensions(raw) {
  if (raw === null || raw === undefined) return null;
  const cleaned = String(raw)
    .split(',')
    .map((s) => s.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);
  return cleaned.length > 0 ? cleaned : null;
}

export default SharePointConnectionForm;
